import { useMutation, useQueryClient } from '@tanstack/react-query';
import { markEmailsReadUnreadV2 } from 'services/mailbox/mailboxServiceV2';
import { useNavbarContext } from 'context/NavbarContext';
import { mailboxV3Keys } from './mailboxV3QueryKeys';

/**
 * useMutation wrapper for marking email threads read/unread with optimistic updates.
 *
 * Optimistic update strategy:
 *   1. onMutate  — cancel in-flight thread queries, snapshot the cache, then patch
 *                  is_unread on all matching threads in every infinite query page.
 *   2. onError   — rollback to the snapshot so the UI is consistent.
 *   3. onSettled — invalidate the full V3 thread list so the server state is
 *                  reconciled on next render regardless of success/failure.
 *
 * Usage:
 *   const { markRead, markUnread, isPending } = useMarkEmailsReadV3(filterKey);
 *   markRead(['thread_id_1', 'thread_id_2']);
 */
const useMarkEmailsReadV3 = (filterKey) => {
  const queryClient = useQueryClient();
  const { notificationData } = useNavbarContext();

  const mutation = useMutation(
    ({ action, email_thread_ids, view_type }) =>
      markEmailsReadUnreadV2({
        action,
        email_thread_ids,
        ...(view_type ? { view_type } : {}),
      }),
    {
      onMutate: async ({ action, email_thread_ids }) => {
        const threadIdSet = new Set(email_thread_ids);
        const isRead = action === 'read';

        // Cancel any outgoing refetches to avoid overwriting our optimistic update
        await queryClient.cancelQueries({ queryKey: mailboxV3Keys.all });

        // Snapshot all thread-list cache entries so we can roll back on error
        const threadListKey = mailboxV3Keys.threads(filterKey);
        const snapshot = queryClient.getQueryData(threadListKey);

        // Optimistically update is_unread on matching threads in the infinite cache
        queryClient.setQueryData(threadListKey, (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: {
                ...page.data,
                email_threads: (page.data?.email_threads ?? []).map((thread) => {
                  const id = thread.thread_id ?? thread.email_thread_id;
                  if (!threadIdSet.has(id)) return thread;
                  return { ...thread, is_unread: !isRead };
                }),
              },
            })),
          };
        });

        return { snapshot, threadListKey };
      },

      onError: (_err, _vars, context) => {
        // Rollback to the snapshot captured in onMutate
        if (context?.snapshot !== undefined) {
          queryClient.setQueryData(context.threadListKey, context.snapshot);
        }
      },

      onSettled: () => {
        // Always reconcile with the server after the mutation settles
        queryClient.invalidateQueries({ queryKey: mailboxV3Keys.all });
        notificationData?.loadNotificationCount();
      },
    }
  );

  const viewType = filterKey.viewType;

  return {
    markRead: (threadIds) => mutation.mutate({ action: 'read', email_thread_ids: threadIds, ...(viewType ? { view_type: viewType } : {}) }),
    markUnread: (threadIds) =>
      mutation.mutate({ action: 'unread', email_thread_ids: threadIds, ...(viewType ? { view_type: viewType } : {}) }),
    isPending: mutation.isLoading,
  };
};

export default useMarkEmailsReadV3;
